import {
  FilterType,
  isValidFilterTypeAndValue,
  isReadOnlyFilterType,
} from "@/lib/filters";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDataTableQuery } from "./useDataTableQuery";
import { useDataTableSettings } from "./useDataTableSettings";

interface UseFilterStateOptions {
  columnId: string;
  defaultFilterType?: FilterType;
}

export const useFilterState = ({
  columnId,
  defaultFilterType = FilterType.IncludesString,
}: UseFilterStateOptions) => {
  const { options } = useDataTableSettings();
  const { query } = useDataTableQuery();

  const [filterType, setFilterType] = useState<FilterType>(defaultFilterType);

  const columnFilterValue = options.getFilterValue(columnId);
  const isFilteredOrSorted = options.isFilteredOrSorted(columnId);

  useEffect(() => {
    if (columnFilterValue?.filter) {
      setFilterType(columnFilterValue?.filter);
    }
  }, [
    columnFilterValue,
    columnFilterValue?.filter,
    columnFilterValue?.value,
    columnId,
    options,
  ]);

  const onFilterTypeChange = useCallback<(nextFilterType: FilterType) => void>(
    (nextFilterType) => {
      const value = columnFilterValue?.value ?? "";

      // Some filter types (e.g. IsEmpty, IsNotEmpty) don't require a value; when invalid, update dropdown only.
      if (!isValidFilterTypeAndValue(nextFilterType, value)) {
        setFilterType(nextFilterType);
        return;
      }

      options.setFilterValue(columnId, {
        value,
        filter: nextFilterType,
      });
    },
    [columnFilterValue?.value, columnId, options, setFilterType],
  );

  const readOnly = useMemo(
    () => isReadOnlyFilterType(filterType),
    [filterType],
  );

  const clearFilter = useCallback(() => {
    options.setFilterValue(columnId, {
      value: "",
      filter: defaultFilterType,
    });
    setFilterType(defaultFilterType);
    options.setFilteredMark(columnId, false);
  }, [options, columnId, defaultFilterType]);

  return {
    filterType,
    onFilterTypeChange,
    readOnly,
    isFilteredOrSorted,
    columnFilterValue,
    query,
    clearFilter,
  };
};
